import { useCallback, useEffect, useState, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, ChevronDown, ChevronRight, FileDown, RefreshCw, Copy, CheckCircle2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const nowMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const emptyEmp = {
  name: "", email: "", designation: "", department: "Engineering",
  joining_date: new Date().toISOString().slice(0, 10),
  ctc_inr: 900000, location: "Hyderabad, IN",
  reporting_to: "", pan: "", bank_account_last4: "",
  create_login: true, temporary_password: "",
};

const fmtINR = (n) => new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", maximumFractionDigits: 0,
}).format(n || 0);

export default function AdminEmployees() {
  const { apiBase, authHeader } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyEmp);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [payslips, setPayslips] = useState({});
  const [payMonth, setPayMonth] = useState({});
  const [payBusy, setPayBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/admin/employees`, { headers: authHeader() });
      setEmployees(data);
    } catch { toast.error("Failed to load employees"); }
    finally { setLoading(false); }
  }, [apiBase, authHeader]);

  useEffect(() => { load(); }, [load]);

  const loadPayslips = async (empId) => {
    try {
      const { data } = await axios.get(
        `${apiBase}/admin/employees/${empId}/payslips`,
        { headers: authHeader() },
      );
      setPayslips((p) => ({ ...p, [empId]: data }));
    } catch { toast.error("Failed to load payslips"); }
  };

  const [onboarding, setOnboarding] = useState({});
  const loadOnboarding = async (empId) => {
    try {
      const { data } = await axios.get(
        `${apiBase}/admin/employees/${empId}/onboarding`,
        { headers: authHeader() },
      );
      setOnboarding((p) => ({ ...p, [empId]: data }));
    } catch { /* optional */ }
  };

  const toggle = async (empId) => {
    const open = !expanded[empId];
    setExpanded((s) => ({ ...s, [empId]: open }));
    if (open && !payslips[empId]) await loadPayslips(empId);
    if (open && !payMonth[empId]) setPayMonth((s) => ({ ...s, [empId]: nowMonth() }));
    if (open && !onboarding[empId]) await loadOnboarding(empId);
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, ctc_inr: Number(form.ctc_inr) || 0 };
      if (!payload.temporary_password) delete payload.temporary_password;
      const { data } = await axios.post(`${apiBase}/admin/employees`, payload, { headers: authHeader() });
      toast.success("Employee added" + (data.temporary_password ? " — password copied to clipboard" : ""));
      if (data.temporary_password) {
        try { await navigator.clipboard.writeText(data.temporary_password); } catch {}
      }
      setForm(emptyEmp); setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  const generatePayslip = async (emp) => {
    setPayBusy(emp.id);
    try {
      const month = payMonth[emp.id] || nowMonth();
      await axios.post(
        `${apiBase}/admin/employees/${emp.id}/payslips`,
        { month, working_days: 22, lop_days: 0 },
        { headers: authHeader() },
      );
      toast.success(`Payslip for ${month} generated`);
      await loadPayslips(emp.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Payslip generation failed");
    } finally { setPayBusy(null); }
  };

  const downloadPayslip = async (ps) => {
    try {
      const resp = await axios.get(`${apiBase}/admin/payslips/${ps.id}/download`, {
        headers: authHeader(), responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${ps.payslip_no}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="admin-employees-panel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="overline">/Admin · HRMS · Employees</span>
          <h1 className="mt-4 font-display text-white text-4xl md:text-5xl tracking-tighter font-light">
            People & payroll
          </h1>
          <p className="mt-3 text-slate-500 text-sm">
            Employees you provision here get a login for the HRMS portal (attendance, leave, payslips).
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={load} disabled={loading}
                  className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white text-sm px-4 py-2.5 rounded-sm"
                  data-testid="admin-employees-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={() => setShowForm((s) => !s)}
                  className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm px-4 py-2.5 rounded-sm"
                  data-testid="admin-employees-new">
            <Plus size={14} /> {showForm ? "Cancel" : "Add employee"}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={create}
              data-testid="admin-employees-form"
              className="mt-6 bg-[#0B132B]/50 border border-white/10 p-5 grid md:grid-cols-2 gap-3">
          <input required placeholder="Full name *" value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })}
                 data-testid="admin-employees-form-name"
                 className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
          <input required type="email" placeholder="Work email *" value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })}
                 data-testid="admin-employees-form-email"
                 className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
          <input required placeholder="Designation *" value={form.designation}
                 onChange={(e) => setForm({ ...form, designation: e.target.value })}
                 className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
          <input required placeholder="Department *" value={form.department}
                 onChange={(e) => setForm({ ...form, department: e.target.value })}
                 className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
          <input required type="date" value={form.joining_date}
                 onChange={(e) => setForm({ ...form, joining_date: e.target.value })}
                 className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
          <input required type="number" min="0" placeholder="Annual CTC (INR) *" value={form.ctc_inr}
                 onChange={(e) => setForm({ ...form, ctc_inr: e.target.value })}
                 data-testid="admin-employees-form-ctc"
                 className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
          <input placeholder="Reporting to" value={form.reporting_to}
                 onChange={(e) => setForm({ ...form, reporting_to: e.target.value })}
                 className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
          <input placeholder="Location" value={form.location}
                 onChange={(e) => setForm({ ...form, location: e.target.value })}
                 className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
          <input placeholder="PAN (optional)" value={form.pan}
                 onChange={(e) => setForm({ ...form, pan: e.target.value })}
                 className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
          <input placeholder="Bank a/c last 4" value={form.bank_account_last4}
                 onChange={(e) => setForm({ ...form, bank_account_last4: e.target.value })}
                 className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
          <input placeholder="Temporary password (auto-generate if blank)" value={form.temporary_password}
                 onChange={(e) => setForm({ ...form, temporary_password: e.target.value })}
                 className="bg-[#040812]/60 border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-500" />
          <label className="flex items-center gap-2 text-slate-300 text-sm">
            <input type="checkbox" checked={form.create_login}
                   onChange={(e) => setForm({ ...form, create_login: e.target.checked })} />
            Create HRMS login for this employee
          </label>
          <div className="md:col-span-2">
            <button type="submit" disabled={busy}
                    data-testid="admin-employees-form-submit"
                    className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-sm px-4 py-2.5 rounded-sm">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Add employee
            </button>
          </div>
        </form>
      )}

      <div className="mt-6 bg-[#0B132B]/40 border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left">
              {["", "Employee", "Designation", "Dept", "Joined", "CTC", "Status"].map((h, i) => (
                <th key={i} className="px-4 py-3 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-12 text-center">
                <Loader2 size={18} className="inline animate-spin text-slate-500" />
              </td></tr>
            )}
            {!loading && employees.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500 text-sm">
                No employees yet. Add your first team member.
              </td></tr>
            )}
            {employees.map((e) => {
              const open = !!expanded[e.id];
              const list = payslips[e.id] || [];
              const month = payMonth[e.id] || nowMonth();
              return (
                <Fragment key={e.id}>
                  <tr data-testid={`admin-employee-row-${e.id}`}
                      className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                    <td className="px-4 py-4 w-8">
                      <button onClick={() => toggle(e.id)}
                              data-testid={`admin-employee-expand-${e.id}`}
                              className="text-slate-400 hover:text-white">
                        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-white">{e.name}</div>
                      <div className="text-slate-500 text-xs">{e.email} · {e.employee_code}</div>
                    </td>
                    <td className="px-4 py-4 text-slate-300 text-xs">{e.designation}</td>
                    <td className="px-4 py-4 text-slate-400 text-xs">{e.department}</td>
                    <td className="px-4 py-4 text-slate-500 text-xs font-mono">{e.joining_date}</td>
                    <td className="px-4 py-4 text-white text-xs font-mono">{fmtINR(e.ctc_inr)}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-sm border font-mono text-[10px] tracking-[0.14em] uppercase ${
                        e.status === "active" ? "bg-green-500/15 text-green-300 border-green-500/40" :
                        e.status === "on-leave" ? "bg-yellow-500/15 text-yellow-300 border-yellow-500/40" :
                                                  "bg-slate-500/15 text-slate-400 border-slate-500/40"
                      }`}>{e.status}</span>
                    </td>
                  </tr>
                  {open && (
                    <tr key={`${e.id}-payslips`} className="bg-[#040812]/60">
                      <td colSpan={7} className="px-6 py-5">
                        {(onboarding[e.id]?.items || []).length > 0 && (() => {
                          const items = onboarding[e.id].items;
                          const done = items.filter((i) => i.done).length;
                          return (
                            <div className="mb-5" data-testid={`admin-onboarding-${e.id}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="overline">Onboarding checklist</div>
                                <div className="font-mono text-[10px] tracking-widest uppercase">
                                  <span className={done === items.length ? "text-green-400" : "text-yellow-300"}>
                                    {done}/{items.length} done
                                  </span>
                                </div>
                              </div>
                              <div className="h-1 bg-white/10 rounded-full overflow-hidden mb-3">
                                <div className="h-full bg-red-500 transition-all"
                                     style={{ width: `${(done / items.length) * 100}%` }} />
                              </div>
                              <ul className="grid md:grid-cols-2 gap-1.5">
                                {items.map((it) => (
                                  <li key={it.key}
                                      className={`flex items-center gap-2 text-xs px-2.5 py-1.5 border ${
                                        it.done
                                          ? "border-green-500/30 text-green-300"
                                          : "border-white/10 text-slate-400"
                                      }`}>
                                    {it.done ? <CheckCircle2 size={11} /> : <span className="w-[11px] h-[11px] border border-slate-600 rounded-full" />}
                                    {it.label}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })()}
                        <div className="flex items-end justify-between gap-3 flex-wrap">
                          <div>
                            <div className="overline mb-2">Payslips</div>
                            {list.length === 0 && (
                              <div className="text-slate-500 text-xs">No payslips generated yet.</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <input type="month" value={month}
                                   onChange={(ev) => setPayMonth((s) => ({ ...s, [e.id]: ev.target.value }))}
                                   data-testid={`admin-employee-payslip-month-${e.id}`}
                                   className="bg-[#0B132B]/60 border border-white/10 text-white text-xs px-3 py-2 rounded-sm focus:outline-none" />
                            <button onClick={() => generatePayslip(e)} disabled={payBusy === e.id}
                                    data-testid={`admin-employee-payslip-generate-${e.id}`}
                                    className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-xs px-3 py-2 rounded-sm">
                              {payBusy === e.id ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                              Generate
                            </button>
                          </div>
                        </div>
                        {list.length > 0 && (
                          <div className="mt-3 grid md:grid-cols-2 gap-2">
                            {list.map((ps) => (
                              <div key={ps.id} data-testid={`admin-employee-payslip-${ps.id}`}
                                   className="flex items-center justify-between bg-[#0B132B]/40 border border-white/10 px-3 py-2.5">
                                <div>
                                  <div className="text-white text-sm">{ps.payslip_no}</div>
                                  <div className="text-slate-500 text-xs font-mono">
                                    {ps.month} · Net {fmtINR(ps.net_pay)}
                                  </div>
                                </div>
                                <button onClick={() => downloadPayslip(ps)}
                                        data-testid={`admin-employee-payslip-download-${ps.id}`}
                                        className="text-slate-400 hover:text-white">
                                  <FileDown size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
